import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import type { CompactOnMobileProps } from '@/lib/types';

// BEC-24926: the gallery is shared with the retailer widget, which keeps its
// current sizing, so every change below is gated on $compactOnMobile rather
// than applied to every consumer.
export const GalleryWrapperCol = styled(Col)<CompactOnMobileProps>`
  position: relative;

  ${({ $compactOnMobile, theme }) => css`
    ${theme.media.md.only} {
      grid-column: span 7;
    }

    ${theme.media.md.down} {
      background: ${`linear-gradient(${theme.colors.lightGrey}, ${theme.colors.tertiaryGrey})`};

      ${$compactOnMobile &&
      css`
        /* The product image shrinks by 10%. Two sizes drive it and both take
           the cut so they stay in proportion: the carousel box (100vw -> 90vw,
           via the variable ImageCarousel reads) and the image itself
           (95vw -> 85.5vw, from ProductCard's carouselImageStyles). */
        --carousel-mobile-height: 90vw;

        /* Both the image and its wrapper carry .carousel-image and are sized at
           95vw, so both take the cut - shrinking only the image would leave it
           centred inside a full-height wrapper, putting the trimmed space back
           above the watch. Three ampersands so this outweighs ProductCard's own
           rule, nested as &&& > &.carousel-image. */
        &&& .swiper-slide .carousel-image {
          height: 85.5vw;
        }

        /* The shared product image carries a 4vw top margin for the PLP card
           grid (ProductCard.styles Img), unset from sm up. Here it lands as a
           white band between the breadcrumb and the watch. */
        && .swiper-slide img,
        && .swiper-slide a > div,
        && .swiper-slide a > div > div {
          margin-top: 0;
        }

        /* The image is now shorter than its slide, so pin it to the top rather
           than letting it centre - centring would put the trimmed space back
           above the watch. */
        && .swiper-slide {
          display: flex;
          align-items: flex-start;
          justify-content: center;
        }
      `}
    }
  `}
`;

export const GalleryIcons = styled(Flex)<CompactOnMobileProps>`
  position: absolute;
  top: 0;
  margin-top: 10px;
  width: calc(100% - ${({ theme }) => theme.spacing(0.25)});
  /* BEC-24926: the product label used to be the first of two children here and
     space-between pushed the icons to the right edge. The label is anchored to
     the image now (see LabelWrapper), leaving one child - space-between would
     park it on the left, so the right edge is asked for directly. */
  justify-content: flex-end;
  align-items: flex-end;

  ${({ $compactOnMobile, theme }) =>
    $compactOnMobile &&
    css`
      ${theme.media.md.down} {
        /* BEC-24926: the wishlist icon shares the breadcrumb's line instead of
           sitting on a row of its own beneath it. The row starts at the very
           top so the icon lines up; the product label is pushed clear of the
           breadcrumb separately (see LabelWrapper). */
        margin-top: 0;
        align-items: flex-start;

        /* The share action is dropped to free vertical space above the
           purchase CTA. Desktop keeps it. */
        .share-wrapper {
          display: none;
        }
      }
    `}

  ${({ theme }) => theme.media.md.up} {
    width: 100%;
    align-items: flex-start;
  }

  .share-wrapper > svg {
    transform: translateX(2px);
  }

  svg {
    width: 26px;
    height: 26px;
    box-sizing: content-box;
    pointer-events: all;
    position: static;
    z-index: ${({ theme }) => theme.zIndex.navigation};
    padding: ${({ theme }) => theme.spacing(1, 2)};
    translate: 2;

    :hover {
      cursor: pointer;

      path,
      circle {
        fill: ${({ theme }) => theme.colors.primaryDark};
        stroke: ${({ theme }) => theme.colors.primaryDark};
      }
    }

    path,
    circle {
      transition: ${({ theme }) => theme.transitions.create('fill')};
      stroke: ${({ theme }) => theme.colors.secondaryGrey};
      fill: transparent;
    }
  }
`;

/** Anchors the product label to the image rather than to the column. */
export const CarouselArea = styled(Box)`
  position: relative;
`;

/**
 * BEC-24926: on the mobile PDP the wishlist icon moves up to share the
 * breadcrumb's line. The label used to sit beside it in that row, so it was
 * dragged onto the crumbs. Offsetting it by a guessed breadcrumb height would
 * break the moment the crumbs wrap, so it is anchored to the top of the image
 * instead - where a product tag belongs - and follows the image wherever it
 * starts. Desktop lands on the same pixel it did before, see margin-top below.
 */
export const LabelWrapper = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  margin: ${({ theme }) => theme.spacing(1, 3)};

  ${({ theme }) => theme.media.md.up} {
    /* On desktop the label used to inherit GalleryIcons' 10px top margin on top
       of its own 8px. The image starts level with that row there - the
       breadcrumb is hidden from md up - so 18px reproduces the old position
       exactly. */
    margin-top: 18px;
  }
`;

export const ActionsWrapper = styled(Flex)<CompactOnMobileProps>`
  ${({ $compactOnMobile, theme }) => css`
    z-index: 2;
    ${theme.media.md.down} {
      transform: translateY(50%);
    }

    ${$compactOnMobile &&
    css`
      ${theme.media.md.down} {
        /* BEC-24926: this half-height nudge dropped the wishlist icon onto its
           own row under the breadcrumb. */
        transform: none;

        /* The wishlist icon's svg carries 16px of horizontal padding, so it is
           wider than the button around it. The share icon used to set this
           column's width and absorb the difference; with share hidden the
           column collapsed to the button and the icon spilled off the right
           edge. Letting the button size to its icon keeps it wide enough. */
        && button {
          width: auto;
        }
      }
    `}
  `}
  && button {
    position: static;
    padding: 0;
  }
`;

export const CenterLoader = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  z-index: 1;
`;
